// Network/connectivity bridge. Uses @capacitor/network on native, navigator.connection on web.
import { Network, type ConnectionStatus } from "@capacitor/network";
import { isNative } from "./platform";
import { drainNativeLocationQueue } from "./location-bg";
import { getCachedTripSync } from "@/lib/offline/trip-cache";

export type ConnectionType = "wifi" | "cellular" | "none" | "unknown";

function mapStatus(s: ConnectionStatus): ConnectionType {
  if (!s.connected) return "none";
  if (s.connectionType === "wifi") return "wifi";
  if (s.connectionType === "cellular") return "cellular";
  if (s.connectionType === "none") return "none";
  return "unknown";
}

export async function getConnectionType(): Promise<ConnectionType> {
  if (isNative()) {
    try { return mapStatus(await Network.getStatus()); } catch { return "unknown"; }
  }
  const conn: any = (navigator as any).connection;
  if (!conn?.type) return "unknown";
  if (conn.type === "wifi") return "wifi";
  if (conn.type === "cellular") return "cellular";
  if (conn.type === "none") return "none";
  return "unknown";
}

export async function isOnWifi(): Promise<boolean> {
  return (await getConnectionType()) === "wifi";
}

/**
 * Guard for the upload worker. Returns true if it's safe to start an upload right now
 * given the user's Wi-Fi-only preference.
 *
 * On native, "unknown" is treated as cellular (block) — safer default off-grid.
 * On web, "unknown" passes through since navigator.connection is unreliable.
 */
export async function canUploadNow(wifiOnlyPref: boolean): Promise<{ ok: boolean; reason?: string }> {
  if (!wifiOnlyPref) return { ok: true };
  const type = await getConnectionType();
  if (type === "wifi") return { ok: true };
  if (type === "unknown") {
    return isNative()
      ? { ok: false, reason: "Connection type unknown — assuming cellular." }
      : { ok: true };
  }
  return { ok: false, reason: `Wi-Fi only is on (current: ${type}).` };
}

/**
 * Subscribe to connection-type changes. Call the returned `remove()` to unsubscribe.
 * On web, polls navigator.connection every 5s as a best-effort fallback.
 */
export function addConnectionListener(cb: (t: ConnectionType) => void): { remove: () => void } {
  if (isNative()) {
    const handle = Network.addListener("networkStatusChange", (s) => cb(mapStatus(s)));
    return { remove: () => { void handle.then((h) => h.remove()); } };
  }
  let last: ConnectionType = "unknown";
  const t = window.setInterval(async () => {
    const cur = await getConnectionType();
    if (cur !== last) { last = cur; cb(cur); }
  }, 5_000);
  return { remove: () => window.clearInterval(t) };
}

/**
 * Captive-portal probe — tiny HEAD to Supabase before we drain the upload queue.
 *
 * Off-grid Wi-Fi (ger camps, hotels) often joins instantly but routes everything
 * to a splash page until you "Accept terms". Without this probe, the BGProcessingTask
 * would happily POST megabytes of photo data into a 200-OK login form.
 *
 * Returns true only if Supabase itself answers with a JSON response — proves
 * DNS + TLS + routing reach the real origin. Captive portals either time out,
 * return HTML with a 200, or hijack to a different host — all caught here.
 */
export async function isSupabaseReachable(timeoutMs = 5_000): Promise<boolean> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key =
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
    (import.meta.env as any).VITE_SUPABASE_ANON_KEY;
  if (!url) return false;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const headers: Record<string, string> = {};
    if (key) {
      headers["apikey"] = key;
      headers["Authorization"] = `Bearer ${key}`;
    }
    const res = await fetch(`${url}/auth/v1/health`, {
      method: "GET",
      signal: ctrl.signal,
      cache: "no-store",
      headers,
    });
    // 2xx = healthy. 401/403 also prove we reached the real backend (not a
    // captive portal hijacking to an HTML splash). Captive portals time out,
    // throw on TLS mismatch, or return 200 HTML from a different host.
    if (res.ok) return true;
    if (res.status === 401 || res.status === 403) return true;
    return false;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * One-shot guard combining Wi-Fi check + captive-portal probe.
 * Call this from the upload worker before reading from upload_queue.
 */
export async function canActuallyUpload(wifiOnlyPref: boolean): Promise<{ ok: boolean; reason?: string }> {
  const conn = await canUploadNow(wifiOnlyPref);
  if (!conn.ok) return conn;
  const reachable = await isSupabaseReachable();
  if (!reachable) return { ok: false, reason: "Network up but Supabase unreachable (captive portal?)." };
  return { ok: true };
}

/**
 * Full background-upload gate including upload mode + battery rules.
 * Returns ok=false (with reason) for manual mode — manual mode disables
 * background drains entirely; user-initiated taps should bypass this gate.
 */
export async function canAutoUpload(opts: {
  wifiOnlyPref: boolean;
  uploadMode: "auto" | "manual";
  minBatteryPct: number;
  requireChargingUnderMin: boolean;
}): Promise<{ ok: boolean; reason?: string }> {
  if (opts.uploadMode === "manual") {
    return { ok: false, reason: "Manual mode — open the Upload screen to send." };
  }
  const net = await canActuallyUpload(opts.wifiOnlyPref);
  if (!net.ok) return net;
  // Lazy import to keep this module free of native deps at module load.
  const { getBatteryInfo, batteryAllowsUpload } = await import("./battery");
  const info = await getBatteryInfo();
  return batteryAllowsUpload(info, opts.minBatteryPct, opts.requireChargingUnderMin);
}

// Module-level: drain the native LocationBG queue whenever the device
// reconnects. Idempotent — drain is a no-op when the queue is empty or the
// plugin isn't registered (web / old shells).
declare global {
  // eslint-disable-next-line no-var
  var __ROAVA_NETWORK_DRAIN_INSTALLED__: boolean | undefined;
}
if (!globalThis.__ROAVA_NETWORK_DRAIN_INSTALLED__) {
  globalThis.__ROAVA_NETWORK_DRAIN_INSTALLED__ = true;
  if (isNative()) {
    void Network.addListener("networkStatusChange", (s) => {
      if (s.connected) {
        void drainNativeLocationQueue(getCachedTripSync()?.id ?? null);
      }
    }).catch(() => { /* ignore */ });
  } else if (typeof window !== "undefined") {
    window.addEventListener("online", () => {
      void drainNativeLocationQueue(getCachedTripSync()?.id ?? null);
    });
  }
}


